// Side-effecting column navigation for Slice 6.
// PRD: Ralph/NavigationalFeatures/PRD.md § Slice 6 (column).
//
// Two paths:
//  - For tables: open the Table Designer in alter mode, seed its SQL buffer
//    from the table's metadata, and pass the column name through
//    `focusColumnName` so the editor can reveal + highlight that column.
//  - For views: open a query tab pre-loaded with the view's CREATE-text. The
//    "scroll to the column-producing expression" refinement is deferred to a
//    follow-up — the tab opens at the top with the full DDL visible, which
//    is the largest piece of value here.
//
// Pure shape: each function returns whether navigation was attempted so the
// DefinitionProvider can stay silent on no-ops without claiming a Location.

import { useWorkspaceStore } from '../../workspace/core/store/workspaceStore';
import { getConnectionById } from '../../connections/utils/connectionLookup';
import { synthesizeAlterTableBuffer } from '../../workspace/table-designer/services/synthesizeBuffer';
import type { DatabaseEngineId, UserFacingEngineId } from '@/core/engines';
import type { QualifiedName } from '../types';
import { defaultSchemaForEngine } from './externalNavigation';
import { safeConnectionAlias, safeConnectionServer } from '@/types/safeConnectionDisplay';

export type ColumnNavigationTarget = {
  readonly relation: QualifiedName;
  readonly column: QualifiedName;
  readonly relationKind: 'table' | 'view';
  readonly engineId: DatabaseEngineId;
  readonly connectionId: string;
  readonly database?: string;
};

export type ColumnNavigationResult = {
  readonly opened: boolean;
  readonly tabId?: string;
  readonly mode?: 'table-designer' | 'view-create-text';
};

/** Read-only probe: would `navigateToColumn(target)` open anything? Mirrors
 *  the connection guard both paths below start with. */
export function canNavigateToColumn(target: ColumnNavigationTarget): boolean {
  return Boolean(getConnectionById(target.connectionId).connection);
}

/** Opens the Table Designer (alter mode) for the relation and signals the
 *  columns grid to scroll + highlight the requested column. Returns the tab
 *  id so tests can assert the tab exists. */
export function navigateToColumn(target: ColumnNavigationTarget): ColumnNavigationResult {
  if (target.relationKind === 'view') {
    return openViewCreateText(target);
  }
  return openTableDesignerForColumn(target);
}

function openTableDesignerForColumn(
  target: ColumnNavigationTarget,
): ColumnNavigationResult {
  const { activeConnection, connection, engine } = getConnectionById(target.connectionId);
  if (!connection) return { opened: false };

  const schema = target.relation.schema ?? defaultSchemaForEngine(target.engineId);
  const database = target.database ?? target.relation.database ?? '';
  const tableName = target.relation.name;
  const columnName = target.column.name;

  const { addTab, updateTabContent } = useWorkspaceStore.getState();
  const tabId = addTab({
    title: `${tableName} [Design]`,
    type: 'table-designer',
    content: {
      mode: 'alter',
      tableName,
      schema,
      database,
      isLoading: true,
      focusColumnName: columnName,
    },
    connectionInfo: {
      connectionId: target.connectionId,
      connectionName: safeConnectionAlias(connection, 'Unnamed Connection'),
      serverHost: safeConnectionServer(activeConnection?.connection),
      database,
    },
  });

  // Fire-and-forget buffer synthesis so the Designer has something to render.
  // We mirror the existing `designTableAction` shape exactly, except for
  // keeping `focusColumnName` set so the focus effect re-runs once the buffer
  // arrives.
  void loadDesignerBuffer({
    tabId,
    connectionId: target.connectionId,
    engine: engine as UserFacingEngineId,
    database,
    schema,
    tableName,
    columnName,
    updateTabContent,
  });

  return { opened: true, tabId, mode: 'table-designer' };
}

function openViewCreateText(target: ColumnNavigationTarget): ColumnNavigationResult {
  const { activeConnection, connection, engine } = getConnectionById(target.connectionId);
  if (!connection) return { opened: false };

  const schema = target.relation.schema ?? defaultSchemaForEngine(target.engineId);
  const database = target.database ?? target.relation.database ?? '';

  const { addTab, getNextQueryNumber, updateTabContent } = useWorkspaceStore.getState();
  const queryNumber = getNextQueryNumber();
  const tabId = addTab({
    title: `${target.relation.name}_CREATE-${queryNumber}`,
    type: 'query',
    content: {
      query: `-- Loading CREATE script for ${schema}.${target.relation.name}...\n`,
      isGeneratingScript: true,
    },
    connectionInfo: {
      connectionId: target.connectionId,
      connectionName: safeConnectionAlias(connection, 'Unnamed Connection'),
      serverHost: safeConnectionServer(activeConnection?.connection),
      database,
    },
  });

  void loadViewDefinition({
    tabId,
    connectionId: target.connectionId,
    database,
    engine,
    schema,
    name: target.relation.name,
    updateTabContent,
  });

  return { opened: true, tabId, mode: 'view-create-text' };
}

type LoadDesignerBufferArgs = {
  tabId: string;
  connectionId: string;
  engine: UserFacingEngineId;
  database: string;
  schema: string;
  tableName: string;
  columnName: string;
  updateTabContent: (tabId: string, patch: Record<string, unknown>) => void;
};

/** Seed the buffer-backed Table Designer the same way `designTableAction`
 *  does — the Designer edits `currentSql` against the `initialSql` baseline,
 *  so a tab left with only column objects renders empty with Save disabled. */
async function loadDesignerBuffer(args: LoadDesignerBufferArgs): Promise<void> {
  const result = await synthesizeAlterTableBuffer(
    {
      engine: args.engine,
      database: args.database,
      schema: args.schema,
      tableName: args.tableName,
    },
    args.connectionId,
  );
  if (result.error) {
    args.updateTabContent(args.tabId, { error: result.error, isLoading: false });
    return;
  }
  args.updateTabContent(args.tabId, {
    initialSql: result.sql ?? undefined,
    currentSql: result.sql ?? undefined,
    isLoading: false,
    focusColumnName: args.columnName,
  });
}

type LoadViewDefinitionArgs = {
  tabId: string;
  connectionId: string;
  database: string;
  engine: string;
  schema: string;
  name: string;
  updateTabContent: (tabId: string, patch: Record<string, unknown>) => void;
};

async function loadViewDefinition(args: LoadViewDefinitionArgs): Promise<void> {
  try {
    const { fetchObjectDefinitionScript } = await import(
      '../../database-objects/services/objectDefinitionService'
    );
    const { script } = await fetchObjectDefinitionScript({
      connectionId: args.connectionId,
      database: args.database,
      engine: args.engine,
      objectType: 'view',
      schema: args.schema,
      name: args.name,
      mode: 'create',
    });
    args.updateTabContent(args.tabId, {
      query: script,
      isGeneratingScript: false,
    });
  } catch (error) {
    args.updateTabContent(args.tabId, {
      query: `-- Error: ${error instanceof Error ? error.message : 'Unknown error'}\n`,
      isGeneratingScript: false,
    });
  }
}
